import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const otherArticles = articles
    .filter((a) => a.id !== article.id && a.category !== article.category)
    .slice(0, 2);

  const categoryLabel =
    article.category === 'news' ? '最新动态' :
    article.category === 'works' ? '代表作品' :
    article.category === 'profile' ? '个人资料' : '粉丝应援';

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shiyaa.cn/' },
            { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://shiyaa.cn/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: categoryLabel, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article>
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-calendar-line mr-1"></i>{article.date}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-user-line mr-1"></i>{article.author}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-snug mb-5">
                  {article.title}
                </h1>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              <div className="rounded-xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-[320px] md:h-[400px] object-cover"
                />
              </div>

              <div
                className="prose-content text-foreground-800 leading-relaxed text-[15px]"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <style>{`
                .prose-content h2 {
                  font-family: var(--font-heading);
                  font-size: 1.35rem;
                  font-weight: 700;
                  color: oklch(var(--foreground-900));
                  margin-top: 2rem;
                  margin-bottom: 0.75rem;
                  padding-bottom: 0.5rem;
                  border-bottom: 1px solid oklch(var(--background-200));
                }
                .prose-content h3 {
                  font-family: var(--font-heading);
                  font-size: 1.1rem;
                  font-weight: 600;
                  color: oklch(var(--foreground-800));
                  margin-top: 1.5rem;
                  margin-bottom: 0.5rem;
                }
                .prose-content p {
                  margin-bottom: 1rem;
                  line-height: 1.85;
                }
                .prose-content ul {
                  list-style: disc;
                  padding-left: 1.5rem;
                  margin-bottom: 1rem;
                }
                .prose-content li {
                  margin-bottom: 0.35rem;
                  line-height: 1.7;
                }
                .prose-content table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 1rem 0 1.5rem;
                }
                .prose-content td {
                  padding: 0.6rem 1rem;
                  border-bottom: 1px solid oklch(var(--background-200));
                  font-size: 0.9rem;
                }
                .prose-content td:first-child {
                  font-weight: 600;
                  color: oklch(var(--foreground-700));
                  width: 140px;
                  white-space: nowrap;
                }
              `}</style>
            </article>

            <div className="mt-14 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900">
                  相关推荐
                </h3>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
                {relatedArticles.length < 3 && otherArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}